import { type StaffImportJobStatus, staffImportJobStatuses } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Link } from "react-router";
import { hintText } from "../../catalog/components/styles";
import { formatDateTime } from "../../catalog/labels";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { EmptyState } from "../../ui/empty-state";
import { SelectField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { TableLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { JobStatusBadge } from "../components/parts";
import { describeImportError } from "../errors";
import { useImportJobList } from "../queries";
import { jobAddress } from "../review/progress";

const statusFilters: readonly Readonly<{ value: StaffImportJobStatus; label: string }>[] = [
  { value: "MAPPING", label: "Drafts" },
  { value: "VALIDATING", label: "Submitted" },
  { value: "REVIEW_REQUIRED", label: "Waiting for review" },
  { value: "PROCESSING", label: "In progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "FAILED", label: "Cancelled or failed" },
];

const isStatus = (value: string): value is StaffImportJobStatus =>
  (staffImportJobStatuses as readonly string[]).includes(value);

type Job = NonNullable<
  ReturnType<typeof useImportJobList>["data"]
>["pages"][number]["items"][number];

const column = createColumnHelper<Job>();

const columns = [
  column.accessor("fileName", {
    header: "File",
    meta: { rowHeader: true },
    cell: (info) => (
      <TableLink
        to={jobAddress(info.row.original)}
        className="font-semibold underline [overflow-wrap:anywhere]"
      >
        {info.getValue()}
      </TableLink>
    ),
  }),
  column.display({
    id: "status",
    header: "Status",
    cell: (info) => <JobStatusBadge job={info.row.original} />,
  }),
  column.accessor("rowCount", { header: "Rows", meta: { className: "tabular-nums" } }),
  column.accessor((job) => job.createdByName ?? "Unknown", {
    id: "createdBy",
    header: "Created by",
    meta: { className: "whitespace-nowrap" },
  }),
  column.accessor((job) => formatDateTime(job.createdAt), {
    id: "created",
    header: "Created",
    meta: { className: "whitespace-nowrap" },
  }),
  column.accessor((job) => formatDateTime(job.updatedAt), {
    id: "lastChanged",
    header: "Last changed",
    meta: { className: "whitespace-nowrap" },
  }),
];

/** The imports: drafts and recent jobs, newest first, a keyset page at a time. */
export function ImportListPage() {
  useWideLayout();
  const [status, setStatus] = useState<StaffImportJobStatus | null>(null);
  const list = useImportJobList(status);
  const jobs = list.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="Imports"
        description="Upload a spreadsheet, map its columns and save a draft. After you submit a draft, review its rows and approve it. Imports create draft items only."
        actions={
          <Button asChild>
            <Link to="/imports/new">
              <Icon name="plus-sign" size="md" />
              New import
            </Link>
          </Button>
        }
      />

      <div className="max-w-72">
        <SelectField
          id="import-status-filter"
          label="Show"
          value={status ?? ""}
          onChange={(value) => setStatus(isStatus(value) ? value : null)}
          options={statusFilters}
          placeholder="All imports"
        />
      </div>

      <div aria-busy={list.isFetching} className="grid min-w-0 gap-4">
        {list.isPending ? (
          <AsyncState
            kind="loading"
            title="Loading the imports"
            message="Reading the first page."
          />
        ) : list.isError ? (
          <AsyncState
            kind="error"
            title="The imports could not be loaded"
            message={describeImportError(list.error).message}
            onRetry={() => void list.refetch()}
          />
        ) : jobs.length === 0 ? (
          <EmptyState
            className="rounded-lg border border-hairline bg-card"
            title="No imports found"
            description={
              status === null
                ? "There are no imports yet. Start with New import."
                : "No import has this status."
            }
          />
        ) : (
          <>
            <DataTable
              banded
              columns={columns}
              data={jobs}
              getRowId={(job) => job.id}
              caption="Imports, newest first"
            />
            <div className="flex flex-wrap items-center gap-4">
              <p role="status" className={hintText}>
                Showing {jobs.length} {jobs.length === 1 ? "import" : "imports"}
                {list.hasNextPage ? ". More are available." : ". That is all of them."}
              </p>
              {list.hasNextPage ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </Button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
